import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConfirmInvoiceInput,
  ConfirmInvoiceResult,
  Invoice,
  ParsedInvoice,
} from '@fleet/shared';
import { api } from '../../lib/apiClient';

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get<{ invoices: Invoice[] }>('/invoices').then((r) => r.invoices),
  });
}

export function parseInvoicePdf(
  file: File,
  type: 'MTC' | 'ONDERHOUD' = 'MTC',
): Promise<ParsedInvoice> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('type', type);
  return api.post<{ pending: ParsedInvoice }>('/invoices/parse', fd).then((r) => r.pending);
}

/** An import changes costs everywhere and can auto-create/link passen. */
function invalidateAfterImport(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['invoices', 'dashboard', 'passen', 'personen', 'cars']) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useConfirmInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmInvoiceInput) => api.post<ConfirmInvoiceResult>('/invoices', input),
    onSuccess: () => invalidateAfterImport(qc),
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/invoices/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateAfterImport(qc),
  });
}

/** Absolute URL to view/download a persisted invoice's stored document. */
export function invoiceDocumentUrl(id: string): string {
  return `/api/invoices/${encodeURIComponent(id)}/document`;
}

/** Fire-and-forget cleanup of an uploaded document whose import was cancelled. */
export function discardDocument(key: string): void {
  void api.post('/invoices/discard-document', { key });
}
